export const WEATHER_LOCATION_KEY = "weather_location_v2";
export const WEATHER_GEO_CACHE_KEY = "weather_geo_cache_v2";

export interface SavedLocation {
  province: string;
  district: string;
}

export interface GeoCoord {
  latitude: number;
  longitude: number;
}

const provinceCoords: Record<string, GeoCoord> = {
  "서울": { latitude: 37.57, longitude: 126.98 },
  "부산": { latitude: 35.18, longitude: 129.08 },
  "대구": { latitude: 35.87, longitude: 128.60 },
  "인천": { latitude: 37.46, longitude: 126.71 },
  "광주": { latitude: 35.16, longitude: 126.85 },
  "대전": { latitude: 36.35, longitude: 127.38 },
  "울산": { latitude: 35.54, longitude: 129.31 },
  "세종": { latitude: 36.48, longitude: 127.29 },
  "경기": { latitude: 37.26, longitude: 127.03 },
  "강원": { latitude: 37.88, longitude: 127.73 },
  "충북": { latitude: 36.64, longitude: 127.49 },
  "충남": { latitude: 36.60, longitude: 126.66 },
  "전북": { latitude: 35.82, longitude: 127.15 },
  "전남": { latitude: 34.99, longitude: 126.48 },
  "경북": { latitude: 36.57, longitude: 128.73 },
  "경남": { latitude: 35.23, longitude: 128.68 },
  "제주": { latitude: 33.50, longitude: 126.53 },
};

function loadGeoCache(): Record<string, GeoCoord> {
  try {
    const raw = localStorage.getItem(WEATHER_GEO_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveGeoCache(cache: Record<string, GeoCoord>): void {
  try {
    localStorage.setItem(WEATHER_GEO_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore
  }
}

export async function getCoordinates(province: string, district: string): Promise<GeoCoord | null> {
  if (district) {
    const cacheKey = `${province}_${district}`;
    const cache = loadGeoCache();
    if (cache[cacheKey]) return cache[cacheKey];

    try {
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(district)}&count=5&language=ko&countryCode=KR`
      );
      if (res.ok) {
        const json = await res.json();
        if (json.results && json.results.length > 0) {
          const coord: GeoCoord = {
            latitude: json.results[0].latitude,
            longitude: json.results[0].longitude,
          };
          saveGeoCache({ ...cache, [cacheKey]: coord });
          return coord;
        }
      }
    } catch {
      // fall through to province coords
    }
  }

  return provinceCoords[province] || null;
}

export function weatherCodeToKorean(code: number): string {
  if (code === 0 || code === 1) return "맑음";
  if (code === 2) return "구름조금";
  if (code === 3 || (code >= 45 && code <= 48)) return "흐림";
  if (
    (code >= 51 && code <= 67) ||
    (code >= 80 && code <= 82)
  ) return "비";
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return "눈";
  if (code >= 95) return "강풍";
  return "맑음";
}

export async function fetchCurrentWeatherText(lat: number, lon: number): Promise<string | null> {
  try {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=weathercode&timezone=Asia%2FSeoul`
    );
    if (!res.ok) return null;
    const json = await res.json();
    const code = json?.current?.weathercode;
    if (code === undefined || code === null) return null;
    return weatherCodeToKorean(code);
  } catch {
    return null;
  }
}

export function loadSavedLocation(): SavedLocation | null {
  try {
    const raw = localStorage.getItem(WEATHER_LOCATION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.province) return parsed as SavedLocation;
    }
  } catch {
    // ignore
  }
  return null;
}